import { useEffect, useRef, useState } from 'react';
import Icon from './Icon';
import { useFeedback } from './FeedbackChat';
import { useAnnouncementAnchor } from './Announcements';
import { useApp } from '../lib/AppContext';
import { captureScreen, savePosition, savedPosition } from '../lib/feedback';

// The floating "Góp ý" button, the same in the personal app (AppShell) and coach mode (CoachShell); `mode` is
// what the team sees it was sent from. 56×56 in the bottom-right corner, 16px above the tab bar. Scrolling down
// tucks it into a 30×52 tab on the edge; it comes back after 1.5 s or on scrolling up (tapping the tab opens the
// chat too). Press and hold to drag it: let go and it sticks to the nearest side, remembered on this device;
// dropped on the × that shows while dragging, it hides (Hoàn tác, or "Hiện nút góp ý" in Cài đặt).
// A red dot means the team replied. Hidden over sheets (capturing a meal, paying, onboarding: any open dialog),
// while the keyboard is up, on someone else's log and before the first-run questions are done.

const SIZE = 56;
const TAB_BAR = 56; // the bottom bars' height, above the safe area
const GAP = 16;
const HOLD_MS = 350;
const SETTLE_MS = 1500;
const HIDE_RADIUS = 48;
const HIDE_ABOVE = 170; // the ×'s centre, from the bottom of the screen

// Shows or hides the button at once and saves it to the account (undone if saving fails).
function useSetShown() {
  const { api } = useApp();
  const { setFeedback } = useFeedback() || {};
  return (show) => {
    setFeedback((f) => ({ ...f, show_button: show }));
    api.patch('/feedback_threads/button', { show }).catch(() => setFeedback((f) => ({ ...f, show_button: !show })));
  };
}

// Any open sheet or dialog, or a field being typed in (the keyboard is up on a phone).
function useCovered() {
  const [covered, setCovered] = useState(false);
  useEffect(() => {
    const check = () => {
      const el = document.activeElement;
      const typing = el && (el.isContentEditable || (/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) && !['checkbox', 'radio', 'button', 'file'].includes(el.type)));
      setCovered(!!document.querySelector('dialog[open]') || !!typing);
    };
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open'], childList: true });
    // Focus moves after focusout: check once it has landed.
    const later = () => setTimeout(check, 0);
    document.addEventListener('focusin', check);
    document.addEventListener('focusout', later);
    return () => {
      observer.disconnect();
      document.removeEventListener('focusin', check);
      document.removeEventListener('focusout', later);
    };
  }, []);
  return covered;
}

// Scrolling down tucks the button away; still for 1.5 s or scrolling up brings it back.
function useTucked() {
  const [tucked, setTucked] = useState(false);
  useEffect(() => {
    let last = window.scrollY;
    let timer;
    const onScroll = () => {
      const y = window.scrollY;
      if (y > last + 4) {
        setTucked(true);
        clearTimeout(timer);
        timer = setTimeout(() => setTucked(false), SETTLE_MS);
      } else if (y < last - 4) {
        clearTimeout(timer);
        setTucked(false);
      }
      last = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); clearTimeout(timer); };
  }, []);
  return tucked;
}

export default function FeedbackButton({ mode }) {
  const { profile, shareCode, editable, coachClient } = useApp();
  const { feedback, openFeedback } = useFeedback() || {};
  const anchor = useAnnouncementAnchor('feedback_button');
  const covered = useCovered();
  const tucked = useTucked();
  const [position, setPosition] = useState(savedPosition);
  const [drag, setDrag] = useState(null); // { x, y } while dragging
  const [opening, setOpening] = useState(false);
  const [toast, setToast] = useState(false);
  const press = useRef(null);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(false), 5000);
    return () => clearTimeout(timer);
  }, [toast]);

  const ready = profile && !profile.needsRole && !profile.needsOnboarding && !shareCode && (editable || coachClient);
  const setShown = useSetShown();

  const open = async () => {
    if (opening) return;
    setOpening(true);
    const screenshot = await captureScreen();
    setOpening(false);
    // A coach looking at a student's log is in coach mode.
    openFeedback({ mode: coachClient ? 'coach' : mode, screenshot });
  };

  // The × to drop on, centred above the tab bar.
  const hideZone = () => ({ x: window.innerWidth / 2, y: window.innerHeight - HIDE_ABOVE });
  const overHideZone = (p) => p && Math.hypot(p.x - hideZone().x, p.y - hideZone().y) < HIDE_RADIUS;

  // A tap opens the chat; holding starts a drag; moving before that is a scroll, not ours.
  const onPointerDown = (e) => {
    const start = { x: e.clientX, y: e.clientY };
    press.current = { start, dragging: false, timer: setTimeout(() => {
      press.current.dragging = true;
      setDrag(start);
      navigator.vibrate?.(10);
    }, HOLD_MS) };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    const p = press.current;
    if (!p) return;
    if (p.dragging) setDrag({ x: e.clientX, y: e.clientY });
    else if (Math.hypot(e.clientX - p.start.x, e.clientY - p.start.y) > 8) {
      clearTimeout(p.timer);
      press.current = null;
    }
  };
  const onPointerUp = (e) => {
    const p = press.current;
    press.current = null;
    if (!p) return;
    clearTimeout(p.timer);
    if (!p.dragging) {
      open();
      return;
    }
    const at = { x: e.clientX, y: e.clientY };
    setDrag(null);
    if (overHideZone(at)) {
      setShown(false);
      setToast(true);
      return;
    }
    const bottom = Math.min(window.innerHeight - 140, Math.max(TAB_BAR + 8, window.innerHeight - at.y - SIZE / 2));
    const next = { side: at.x < window.innerWidth / 2 ? 'left' : 'right', bottom: Math.round(bottom) };
    setPosition(next);
    savePosition(next);
  };
  const onPointerCancel = () => {
    if (press.current) clearTimeout(press.current.timer);
    press.current = null;
    setDrag(null);
  };

  if (!feedback || !ready) return null;
  const undo = toast && (
    <div role="status" data-feedback-ui className="fixed inset-x-4 bottom-28 z-40 mx-auto flex max-w-sm items-center justify-between gap-3 rounded-2xl bg-[#17201b] px-4 py-3 text-sm font-semibold text-white">
      <span>Đã ẩn nút góp ý · Bật lại trong Cài đặt</span>
      <button type="button" className="min-h-9 shrink-0 text-[#6fd3a0] underline" onClick={() => { setShown(true); setToast(false); }}>Hoàn tác</button>
    </div>
  );
  if (!feedback.show_button || covered) return undo || null;

  const bottom = position.bottom != null ? `${position.bottom}px` : `calc(env(safe-area-inset-bottom, 0px) + ${TAB_BAR + GAP}px)`;
  const unread = feedback.unread > 0;
  const small = tucked && !drag;
  const style = drag
    ? { left: drag.x - SIZE / 2, top: drag.y - SIZE / 2 }
    : { bottom, [position.side]: small ? 0 : GAP };
  const shape = small
    ? `h-[52px] w-[30px] ${position.side === 'right' ? 'rounded-l-[16px]' : 'rounded-r-[16px]'}`
    : 'size-14 rounded-full';

  return (
    <>
      <button ref={anchor} type="button" data-feedback-ui aria-label={unread ? 'Góp ý cho Macro Mate, có trả lời mới' : 'Góp ý cho Macro Mate'}
        onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerCancel}
        onContextMenu={(e) => e.preventDefault()}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
        style={{ ...style, touchAction: 'none', WebkitTouchCallout: 'none', userSelect: 'none' }}
        className={`fixed ${drag ? 'z-50' : 'z-30'} grid place-items-center bg-[#17804f] text-white shadow-lg transition-[width,height,border-radius] ${shape} ${drag ? 'scale-110' : 'active:scale-95'}`}>
        {opening ? <span className="loading loading-spinner loading-sm" /> : <Icon name="chat" className={small ? 'size-4' : 'size-6'} />}
        {unread && <span className={`absolute size-3 rounded-full bg-error ring-2 ring-base-100 ${small ? 'right-1 top-1' : 'right-0.5 top-0.5'}`} />}
      </button>
      {drag && (
        <div data-feedback-ui className="pointer-events-none fixed inset-x-0 bottom-0 z-40 h-80 bg-gradient-to-t from-black/60 to-transparent">
          <div className="absolute inset-x-0 flex flex-col items-center" style={{ bottom: HIDE_ABOVE - SIZE / 2 - 24 }}>
            <span className={`grid size-14 place-items-center rounded-full border-2 border-white text-white transition ${overHideZone(drag) ? 'scale-125 bg-white/30' : ''}`}>
              <Icon name="x" className="size-6" />
            </span>
            <span className="mt-2 text-xs font-semibold text-white">Thả vào đây để ẩn nút</span>
          </div>
        </div>
      )}
      {undo}
    </>
  );
}

// "Hiện nút góp ý" (Cài đặt and coach mode's Tài khoản): brings the button back after it was dragged away.
export function FeedbackToggle() {
  const { feedback } = useFeedback() || {};
  const set = useSetShown();
  if (!feedback) return null;
  return (
    <label className="flex min-h-14 items-center justify-between gap-3 py-2">
      <span className="flex items-center gap-3">
        <Icon name="chat" className="size-5 shrink-0" />
        <span>
          <span className="block text-[15px] font-medium">Hiện nút góp ý</span>
          <span className="block text-xs text-muted">Nút nổi để báo lỗi, góp ý và xem trả lời</span>
        </span>
      </span>
      <input type="checkbox" className="toggle toggle-success" checked={!!feedback.show_button} onChange={(e) => set(e.target.checked)} />
    </label>
  );
}
